

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";

export function AuditForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [asinsOrUrl, setAsinsOrUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Reject more than 20 ASINs client-side so the user knows immediately
    // instead of being silently truncated server-side.
    const asinMatches = asinsOrUrl.match(/B0[A-Z0-9]{8}|[0-9]{9}[0-9X]/g) ?? [];
    const uniqueCount = new Set(asinMatches).size;
    if (uniqueCount > 20) {
      setError(
        `You pasted ${uniqueCount} ASINs. Audits are capped at 20. For larger catalogs, book a call: cal.com/maximiliang/demo`
      );
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch("/api/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, input: asinsOrUrl }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const isServerError = res.status >= 500;
        const userMessage =
          !isServerError && typeof data.error === "string"
            ? data.error
            : "Something went wrong on our end. Please try again in a few minutes.";
        setError(userMessage);
        setSubmitting(false);
        return;
      }

      const { id } = await res.json();
      track("audit_submitted", { audit_id: id });
      navigate(`/audit/${id}`);
    } catch {
      setError(
        "We couldn't reach our server. Check your connection and try again."
      );
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-slate-900">
          Where should we send the results?
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
        />
      </div>

      <div>
        <label htmlFor="asins" className="block text-sm font-medium text-slate-900">
          ASINs or storefront URL
        </label>
        <textarea
          id="asins"
          required
          rows={4}
          value={asinsOrUrl}
          onChange={(e) => setAsinsOrUrl(e.target.value)}
          placeholder={"Paste up to 20 ASINs, one per line:\nB0ABC12345\nB0DEF67890\n\nOr paste your Amazon storefront URL."}
          className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2.5 text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900 font-mono text-sm"
        />
        <p className="mt-2 text-xs text-slate-500">
          We&rsquo;ll check a sample of public reviews from each product.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-white font-medium shadow-sm hover:bg-slate-800 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Starting your audit…" : "Run my free audit"}
      </button>

      <p className="text-xs text-slate-500 text-center">
        No credit card. No account required.
      </p>
    </form>
  );
}
